import type { Asset } from '../types/domain';
import { isAssetExtractionActive } from '../utils/assetDisplay';
import { assetService } from './assetService';

export type ExtractionHandoffOutcome = 'started' | 'pas_skipped' | 'failed' | 'uncertain';

export type ExtractionHandoffResult = {
  startedIds: string[];
  pasSkippedIds: string[];
  failedIds: string[];
  uncertainIds: string[];
};

function isTransportFailure(message: string) {
  return /timeout|超时|network|fail|连接|socket|ECONN|ENOTFOUND|Failed to fetch|AbortError/i.test(
    String(message || ''),
  );
}

/**
 * Confirm extract enqueue only — does not re-upload.
 * Safe to retry: AppFunctor returns existing queued/processing job when present.
 */
export async function handoffKnowledgeExtraction(
  userId: string,
  asset: Asset,
): Promise<ExtractionHandoffOutcome> {
  if (!asset?.id) return 'failed';

  try {
    const extractionResult = await assetService.triggerKnowledgeExtraction({
      userId,
      assetId: asset.id,
      ossId: asset.oss_id,
    });

    if (
      extractionResult
      && typeof extractionResult === 'object'
      && 'skippedPasSuspected' in extractionResult
      && extractionResult.skippedPasSuspected === true
    ) {
      return 'pas_skipped';
    }

    if (
      extractionResult
      && typeof extractionResult === 'object'
      && 'accepted' in extractionResult
      && extractionResult.accepted === true
      && typeof extractionResult.taskId === 'string'
    ) {
      return 'started';
    }

    const refreshed = await assetService.getAsset(asset.id).catch(() => asset);
    return isAssetExtractionActive(refreshed) ? 'started' : 'uncertain';
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error || '');
    if (/MATERIAL_EXTRACTING|正在提取/.test(message)) {
      return 'started';
    }
    if (!isTransportFailure(message)) {
      console.warn('[extractionHandoff] failed', asset.id, error);
      return 'failed';
    }
    const refreshed = await assetService.getAsset(asset.id).catch(() => null);
    if (refreshed && isAssetExtractionActive(refreshed)) {
      return 'started';
    }
    console.warn('[extractionHandoff] uncertain after transport error', asset.id, error);
    return 'uncertain';
  }
}

export async function handoffUploadedAssets(
  userId: string,
  assets: Asset[],
): Promise<ExtractionHandoffResult> {
  const list = (assets || []).filter((asset) => asset?.id);
  const startedIds: string[] = [];
  const pasSkippedIds: string[] = [];
  const failedIds: string[] = [];
  const uncertainIds: string[] = [];

  await Promise.all(list.map(async (asset) => {
    const outcome = await handoffKnowledgeExtraction(userId, asset);
    if (outcome === 'started') startedIds.push(asset.id);
    else if (outcome === 'pas_skipped') pasSkippedIds.push(asset.id);
    else if (outcome === 'failed') failedIds.push(asset.id);
    else uncertainIds.push(asset.id);
  }));

  return { startedIds, pasSkippedIds, failedIds, uncertainIds };
}
